import React, { memo, useCallback, useEffect, useState } from 'react';
import { Spin } from 'antd5';
import { LoadingOutlined } from '@ant-design/icons';

import { FormValue } from './page-review';

import { getByUrl } from '../../../../apis/public/master-tables';
import { IElementDataSource, FormComponentType } from '../../../../types/form';
import { IMasterTableData } from '../../../../types/models/master-table';
import { IPersona } from '../../../../types/models/persona';
import useMountedState from '../../../../hooks/use-mounted-state';
import { convertMasterTableDataToPersona } from '../../../../utils/form';
import { MultiSelectPhotos } from '../..';
import { constructDataSourceUrl } from '../../../../utils/form-builder';

const INITIAL_DATA: IMasterTableData[][] = [];

type FormValueProps = {
  dataSource: IElementDataSource;
  type?: FormComponentType;
  children?: any;
};

type State = {
  data: IMasterTableData[][];
  isLoading: boolean;
};

function getDisplayValue(value: string | string[]) {
  if (Array.isArray(value)) {
    return value.join('\n');
  }

  return value;
}

function displayCards(sourceData: IMasterTableData[][]) {
  const personas: IPersona[] = sourceData.reduce((acc: IPersona[], data: IMasterTableData[]): any => {
    return acc.concat(data.map(convertMasterTableDataToPersona));
  }, []);
  const value: string[] = personas.map(({ id }) => id);

  return <MultiSelectPhotos name="review_personas" value={value} personas={personas} />;
}

const FormValueWithDataSource = memo(function FormValueWithDataSource({ dataSource, type, children }: FormValueProps) {
  const { url, titleName, transformTitle } = dataSource;
  const isMounted = useMountedState();
  const [state, setState] = useState<State>({
    data: INITIAL_DATA,
    isLoading: true,
  });

  const setStateWhenMounted = useCallback(
    (newState) => {
      if (isMounted()) {
        setState((prevState) => ({ ...prevState, ...newState }));
      }
    },
    [isMounted]
  );

  useEffect(() => {
    const fullUrl = constructDataSourceUrl(url);

    async function fetch() {
      try {
        const value = Array.isArray(children) ? children : [children];
        const data = await Promise.all(value.map((item) => getByUrl(fullUrl, { id: item as string })));
        setStateWhenMounted({ data });
      } catch (err) {
        // no-op
      } finally {
        setStateWhenMounted({ isLoading: false });
      }
    }
    if (children) {
      fetch();
    } else {
      setStateWhenMounted({ isLoading: false });
    }
  }, [children, setStateWhenMounted, url]);

  const getValue = useCallback(() => {
    if (!children) {
      return children;
    }
    const { data, isLoading } = state;
    const sourceData = data || INITIAL_DATA;
    if (Array.isArray(sourceData)) {
      if (sourceData.length === 0) {
        return isLoading ? ' ' : getDisplayValue(children);
      } else if (type === FormComponentType.MultiSelectPhotos) {
        return displayCards(sourceData);
      } else {
        return sourceData
          .map((data: IMasterTableData[]) => {
            return data.map((item) =>
              transformTitle ? transformTitle(item[titleName], item) : getDisplayValue(item[titleName] as string)
            );
          })
          .join('\n');
      }
    } else {
      console.error('dataSource currently only supports array of data');
      return children;
    }
  }, [children, state, titleName, transformTitle, type]);

  const value = getValue();

  return (
    <FormValue>
      <Spin spinning={state.isLoading} indicator={<LoadingOutlined />} style={{ width: 0, marginLeft: '10px' }}>
        {value}
      </Spin>
    </FormValue>
  );
});

export default FormValueWithDataSource;
